import { RobinSkeleton } from 'components/ui/molecules/RobinSkeleton';
import { AnimatePresence } from 'framer-motion';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { CampaignJobboard } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import { useStyles } from './CandidateCVIframe.styles';

export const CandidateCardCvIframe = memo(
  ({
    cvFileUrl,
    retryWithinMilliseconds = 5000,
    shouldUseGoogleDocs = true,
  }: {
    cvFileUrl: string;
    retryWithinMilliseconds?: number;
    activeJobBoard?: CampaignJobboard;
    shouldUseGoogleDocs?: boolean;
  }) => {
    const classes = useStyles({ shouldUseGoogleDocs });
    const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
    const [urlTimestamp, setUrlTimestamp] = useState(Date.now());
    const [isLoadingIframe, setIsLoadingIframe] = useState(true);
    const { dictionary } = useContext(LocalizationContext);

    const isLoadingIframeRef = useRef(isLoadingIframe);

    useEffect(() => {
      isLoadingIframeRef.current = isLoadingIframe;
    }, [isLoadingIframe]);

    useEffect(() => {
      if (!iframeRef) return;

      const handleLoad = () => {
        setIsLoadingIframe(false);
      };

      iframeRef.addEventListener('load', handleLoad);

      const reloadIframeAfterTimeout = setTimeout(() => {
        if (isLoadingIframeRef.current) {
          setUrlTimestamp(Date.now());
          return;
        }
      }, retryWithinMilliseconds);

      return () => {
        iframeRef.removeEventListener('load', handleLoad);
        clearTimeout(reloadIframeAfterTimeout);
      };
    }, [urlTimestamp, iframeRef]);

    const url = shouldUseGoogleDocs
      ? `https://docs.google.com/viewer?embedded=true&url=${cvFileUrl}&printtitle=false&t=${urlTimestamp}`
      : cvFileUrl;

    return (
      <div css={classes.root}>
        <iframe ref={setIframeRef} css={classes.iframe} sandbox="allow-scripts allow-same-origin" src={url} />
        <div css={classes.hideOpenAnotherPageIframeButton} />
        <AnimatePresence>
          {isLoadingIframe && <RobinSkeleton loadingMessage={dictionary.loadingCandidateCv} css={classes.skeleton} />}
        </AnimatePresence>
      </div>
    );
  },
);
